// Artifacts tab — three cards mapping to the three things the homeowner
// can actually do with their case:
//
//   1) Free firm message — generate today, send to the contractor.
//   2) Full case pack (€149) — every paid document bundled. Once unlocked
//      the user gets the case file, the registered notice, the routing
//      recommendation, an evidence ZIP, and the lawyer handoff one-pager.
//   3) Talk to a partner lawyer — coming soon, listed so the user knows
//      it's the eventual escalation path AND so it acts as the pricing
//      anchor (lawyer hourly rates make €149 read as the budget option).
//
// The pack card is the one we're trying to sell. It carries an anchor
// strip (lawyer-from-scratch / DIY / us), a per-line "saves Xh" caption
// on every bundled artifact, a sunk-cost line that surfaces what the
// user has already done, and a sample-PDF preview link wired to the
// existing /api/pdf/sample endpoint.
//
// Backend stays untouched: each kind still gets its own artifact row,
// generation endpoints are per-kind. The "Generate my pack" CTA stays
// disabled until Polar.sh checkout lands.

import { useState } from 'react';
import {
  type CaseArtifact,
  type CaseArtifactKind,
  downloadArtifactUrl,
  useArtifacts,
} from '../artifacts';
import { type ArtifactReadiness, useCaseFacts } from '../case-facts';
import { useDismissedGaps } from '../evidence';
import { useFiles } from '../files';
import { currentLocale } from '../locale';
import { m } from '../paraglide/messages';
import { useInconsistencies, useTimeline } from '../timeline';
import { LetterPreviewModal } from './letter-preview';
import { LogOutcomeStrip } from './log-outcome-banner';

// Kinds bundled into the paid pack. Order matches what we'd hand off to
// a lawyer — case file first, the formal notice, the routing
// recommendation, evidence, and the one-pager last.
const PACK_KINDS: CaseArtifactKind[] = [
  'case_brief',
  'registered_notice',
  'routing_letter',
  'evidence_zip',
  'lawyer_handoff',
];

type ProgressCounts = {
  events: number;
  files: number;
  answered: number;
};

export function ArtifactsPane({
  caseId,
  forceOpenLogOutcome = false,
  onCloseForceOpenLogOutcome,
}: {
  caseId: string;
  /** When the case-detail route receives `?log=outcome` (the deep link
   * sent in the 24h follow-up email), the outcome modal auto-opens. */
  forceOpenLogOutcome?: boolean;
  onCloseForceOpenLogOutcome?: () => void;
}) {
  const artifacts = useArtifacts(caseId);
  const facts = useCaseFacts(caseId);
  const timeline = useTimeline(caseId);
  const files = useFiles(caseId);
  const inconsistencies = useInconsistencies(caseId);

  // Dismissed inline gaps (from the timeline ⚠ "I don't need to act on
  // this" affordance) must subtract from the pack's readiness gate —
  // otherwise the user dismisses on the timeline but still sees the pack
  // card stuck on "Almost ready, attach a doc".
  const { dismissed: dismissedGaps } = useDismissedGaps(caseId);
  const byKind = new Map<CaseArtifactKind, CaseArtifact>();
  for (const a of artifacts.data ?? []) {
    if (!byKind.has(a.kind)) byKind.set(a.kind, a);
  }
  const readiness = facts.data?.readiness ?? null;
  const progress: ProgressCounts = {
    events: timeline.data?.length ?? 0,
    files: files.data?.length ?? 0,
    answered: (inconsistencies.data ?? []).filter((i) => !!i.homeowner_note).length,
  };

  return (
    <div className="artifacts-pane">
      <p className="artifacts-intro small muted">{m.artifactsIntro()}</p>
      <div className="artifacts-cards">
        <FreeMessageCard
          caseId={caseId}
          forceOpenLogOutcome={forceOpenLogOutcome}
          onCloseForceOpenLogOutcome={onCloseForceOpenLogOutcome}
        />
        <PackCard
          caseId={caseId}
          byKind={byKind}
          readiness={readiness}
          progress={progress}
          dismissedGaps={dismissedGaps}
        />
        <LawyerCard />
      </div>
    </div>
  );
}

// ── Card 1 ─────────────────────────────────────────────────────────────────
// Free firm-message email. The free tier — clearly marked. The user can
// generate it as many times as they like as the case evolves.

function FreeMessageCard({
  caseId,
  forceOpenLogOutcome = false,
  onCloseForceOpenLogOutcome,
}: {
  caseId: string;
  forceOpenLogOutcome?: boolean;
  onCloseForceOpenLogOutcome?: () => void;
}) {
  const facts = useCaseFacts(caseId);
  const readiness = facts.data?.readiness?.firm_message ?? null;
  const factsReady = readiness?.ready ?? true;
  const factsMissing = readiness?.missing ?? [];
  const [open, setOpen] = useState(false);

  return (
    <article className="artifact-card">
      <header className="artifact-card-head">
        <h3 className="artifact-card-title">{m.artifactFirmMessageTitle()}</h3>
        <span className="artifact-tier artifact-tier-free">{m.artifactsTierFree()}</span>
      </header>
      <p className="artifact-card-body">{m.artifactFirmMessageDescriptionV2()}</p>
      {!factsReady && factsMissing.length > 0 ? (
        <p className="artifact-card-gap small">
          {m.artifactsNeedsPrefix()} {factsMissing.map((f) => f.label).join(', ')}.
        </p>
      ) : null}
      <div className="artifact-card-foot">
        <button
          type="button"
          className="btn-primary"
          onClick={() => setOpen(true)}
          disabled={!factsReady}
        >
          {m.letterPreviewOpenCta()}
        </button>
      </div>
      <LogOutcomeStrip
        caseId={caseId}
        forceOpen={forceOpenLogOutcome}
        onCloseAfterOpen={onCloseForceOpenLogOutcome}
      />
      <LetterPreviewModal caseId={caseId} open={open} onClose={() => setOpen(false)} />
    </article>
  );
}

// ── Card 2 ─────────────────────────────────────────────────────────────────
// The full case pack. The hero card. One product, one price, one CTA —
// but framed against what the user would otherwise spend (lawyer hours,
// DIY weeks) and grounded in the work they've already done in the
// workspace, so the €149 reads as the obvious next step rather than a
// fresh purchase decision.

function PackCard({
  caseId,
  byKind,
  readiness,
  progress,
  dismissedGaps,
}: {
  caseId: string;
  byKind: Map<CaseArtifactKind, CaseArtifact>;
  readiness: Record<string, ArtifactReadiness> | null;
  progress: ProgressCounts;
  dismissedGaps: Set<string>;
}) {
  const aggregate = aggregatePackReadiness(readiness, dismissedGaps);
  const isUnlocked = false; // TODO: wire to cases.paid_at when payment lands
  const ready = aggregate.factsMissing.length === 0 && aggregate.evidenceGapCount === 0;

  return (
    <article className="artifact-card artifact-card-pack">
      <p className="artifact-card-eyebrow">{m.artifactPackEyebrow()}</p>
      <header className="artifact-card-head">
        <h3 className="artifact-card-title">{m.artifactPackTitle()}</h3>
        <span className="artifact-tier artifact-tier-paid">{m.artifactPackPrice()}</span>
      </header>
      <p className="artifact-card-body">{m.artifactPackDescription()}</p>
      <p className="artifact-card-headline">{m.artifactPackTimeSavedHeadline()}</p>

      <PackAnchorStrip />

      <PackProgressLine progress={progress} />

      <ul className="artifact-pack-contents">
        <PackItem
          label={m.artifactCaseBriefTitle()}
          savesHint={m.artifactCaseBriefTimeSaved()}
          isUnlocked={isUnlocked}
          latest={byKind.get('case_brief')}
          caseId={caseId}
        />
        <PackItem
          label={m.artifactRegisteredNoticeTitle()}
          savesHint={m.artifactRegisteredNoticeTimeSaved()}
          isUnlocked={isUnlocked}
          latest={byKind.get('registered_notice')}
          caseId={caseId}
        />
        <PackItem
          label={m.artifactRoutingLetterTitle()}
          savesHint={m.artifactRoutingLetterTimeSaved()}
          isUnlocked={isUnlocked}
          latest={byKind.get('routing_letter')}
          caseId={caseId}
        />
        <PackItem
          label={m.artifactEvidenceZipTitle()}
          savesHint={m.artifactEvidenceZipTimeSaved()}
          isUnlocked={isUnlocked}
          latest={byKind.get('evidence_zip')}
          caseId={caseId}
        />
        <PackItem
          label={m.artifactLawyerHandoffTitle()}
          savesHint={m.artifactLawyerHandoffTimeSaved()}
          isUnlocked={isUnlocked}
          latest={byKind.get('lawyer_handoff')}
          caseId={caseId}
        />
      </ul>

      <SamplePreviewLink />

      <PackStatus
        ready={ready}
        factsMissing={aggregate.factsMissing}
        evidenceGapCount={aggregate.evidenceGapCount}
      />

      <div className="artifact-card-foot artifact-card-foot-stack">
        <button type="button" className="btn-primary artifact-pack-cta" disabled>
          {m.artifactPackCtaPrimary()}
        </button>
        <p className="artifact-card-subtext small muted">{m.artifactPackCtaSubtext()}</p>
      </div>
    </article>
  );
}

// Status panel that sits between the pack contents and the CTA. Two
// states: ready (single calm "all facts captured" line) or almost-ready
// (forward-looking list of what to finish, each item a link that jumps
// to the tab where the user can complete it). Reframing "Needs: X" as
// "Add X →" matters: missing-facts language reads as failure right next
// to a disabled button. Forward-looking list reads as remaining steps.
function PackStatus({
  ready,
  factsMissing,
  evidenceGapCount,
}: {
  ready: boolean;
  factsMissing: Array<{ path: string; label: string }>;
  evidenceGapCount: number;
}) {
  if (ready) {
    return <p className="artifact-card-ready small">{m.artifactPackReady()}</p>;
  }
  if (factsMissing.length === 0 && evidenceGapCount === 0) return null;

  // Each missing fact carries the path so the Data pane can scroll to,
  // focus, and flash that exact field. The evidence link routes to the
  // Timeline tab and flashes every proof-missing event so the user sees
  // *which* events need backing — the previous version dropped them on
  // Sources with no orientation, which felt broken.
  function jumpToFact(path: string) {
    window.dispatchEvent(
      new CustomEvent('claimpack:switch-tab', {
        detail: { tab: 'data', target: { kind: 'fact', path } },
      }),
    );
  }
  function jumpToEvidence() {
    window.dispatchEvent(
      new CustomEvent('claimpack:switch-tab', {
        detail: { tab: 'timeline', target: { kind: 'evidence' } },
      }),
    );
  }

  return (
    <div className="artifact-pack-status">
      <p className="artifact-pack-status-heading small">{m.artifactPackAlmostReady()}</p>
      <ul className="artifact-pack-status-list">
        {factsMissing.map((f) => (
          <li key={f.path}>
            <button
              type="button"
              className="link-button artifact-pack-status-link"
              onClick={() => jumpToFact(f.path)}
            >
              {m.artifactPackFinaliseFact({ label: f.label })}
            </button>
          </li>
        ))}
        {evidenceGapCount > 0 ? (
          <li>
            <button
              type="button"
              className="link-button artifact-pack-status-link"
              onClick={jumpToEvidence}
            >
              {m.artifactPackFinaliseEvidence({ count: String(evidenceGapCount) })}
            </button>
          </li>
        ) : null}
      </ul>
    </div>
  );
}

function PackAnchorStrip() {
  return (
    <div className="artifact-pack-anchor">
      <p className="artifact-pack-anchor-heading small muted">{m.artifactPackAnchorHeading()}</p>
      <ul className="artifact-pack-anchor-list">
        <li className="artifact-pack-anchor-row artifact-pack-anchor-row-alt">
          <span className="artifact-pack-anchor-text">{m.artifactPackAnchorLawyer()}</span>
        </li>
        <li className="artifact-pack-anchor-row artifact-pack-anchor-row-alt">
          <span className="artifact-pack-anchor-text">{m.artifactPackAnchorDIY()}</span>
        </li>
        <li className="artifact-pack-anchor-row artifact-pack-anchor-row-us">
          <span className="artifact-pack-anchor-text">{m.artifactPackAnchorUs()}</span>
        </li>
      </ul>
    </div>
  );
}

function PackProgressLine({ progress }: { progress: ProgressCounts }) {
  const empty = progress.events === 0 && progress.files === 0;
  return (
    <div className="artifact-pack-progress">
      <p className="artifact-pack-progress-heading small muted">
        {m.artifactPackProgressHeading()}
      </p>
      <p className="artifact-pack-progress-body small">
        {empty
          ? m.artifactPackProgressEmpty()
          : progress.answered > 0
            ? m.artifactPackProgressDone({
                events: String(progress.events),
                files: String(progress.files),
                answered: String(progress.answered),
              })
            : m.artifactPackProgressDoneNoAnswers({
                events: String(progress.events),
                files: String(progress.files),
              })}
      </p>
    </div>
  );
}

// Sample-PDF preview. The case-brief sample is the most powerful single
// asset we can show before payment — it lets the user "hold" the artefact
// before deciding. We POST to the existing /api/pdf/sample endpoint
// (auth-gated, returns the canned brief), turn the response into a blob
// URL, and open it in a new tab. apiFetch wants JSON so we use raw fetch
// here. The blob URL is revoked on a timer to avoid the leak; if the tab
// is still open after 60s the browser has cached the bytes.
function SamplePreviewLink() {
  const [state, setState] = useState<'idle' | 'loading' | 'error'>('idle');
  async function open() {
    setState('loading');
    try {
      const res = await fetch('/api/pdf/sample', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-UI-Locale': currentLocale() },
      });
      if (!res.ok) throw new Error(`pdf/sample ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      setState('idle');
    } catch {
      setState('error');
    }
  }
  return (
    <p className="artifact-pack-sample">
      <button type="button" className="link-button" onClick={open} disabled={state === 'loading'}>
        {state === 'loading' ? m.artifactPackSampleLoading() : m.artifactPackSamplePreview()}
      </button>
      {state === 'error' ? (
        <span className="artifact-pack-sample-error small file-extraction-err">
          {' '}
          · {m.artifactPackSampleError()}
        </span>
      ) : null}
    </p>
  );
}

function PackItem({
  label,
  savesHint,
  isUnlocked,
  latest,
  caseId,
}: {
  label: string;
  savesHint: string;
  isUnlocked: boolean;
  latest: CaseArtifact | undefined;
  caseId: string;
}) {
  return (
    <li className="artifact-pack-item">
      <div className="artifact-pack-item-text">
        <span className="artifact-pack-item-label">{label}</span>
        <span className="artifact-pack-item-saves small muted">{savesHint}</span>
      </div>
      {isUnlocked && latest?.status === 'ready' && latest.id ? (
        <a
          className="artifact-pack-item-link"
          href={downloadArtifactUrl(caseId, latest.id)}
          target="_blank"
          rel="noreferrer noopener"
        >
          {m.artifactsDownloadAgain()}
        </a>
      ) : null}
    </li>
  );
}

// ── Card 3 ─────────────────────────────────────────────────────────────────
// Talk to a partner lawyer. Currently unavailable. Repositioned to act
// as the pricing anchor — surfacing the hourly-rate range makes the
// €149 pack above read as the budget option rather than an unbenchmarked
// charge.

function LawyerCard() {
  return (
    <article className="artifact-card artifact-card-soon">
      <header className="artifact-card-head">
        <h3 className="artifact-card-title">{m.artifactHireLawyerTitle()}</h3>
        <span className="artifact-tier artifact-tier-soon">{m.artifactsComingSoon()}</span>
      </header>
      <p className="artifact-card-body">{m.artifactHireLawyerDescription()}</p>
      <p className="artifact-card-anchor small muted">{m.artifactHireLawyerAnchor()}</p>
    </article>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────

function aggregatePackReadiness(
  readiness: Record<string, ArtifactReadiness> | null,
  dismissedGaps: Set<string>,
): {
  factsMissing: Array<{ path: string; label: string }>;
  evidenceGapCount: number;
} {
  if (!readiness) return { factsMissing: [], evidenceGapCount: 0 };

  // Dedup missing facts across all paid kinds — the user shouldn't see
  // "Contractor NIF" five times.
  const seenPaths = new Set<string>();
  const factsMissing: Array<{ path: string; label: string }> = [];
  let maxEvidenceGaps = 0;

  for (const kind of PACK_KINDS) {
    const r = readiness[kind];
    if (!r) continue;
    for (const m of r.missing) {
      if (!seenPaths.has(m.path)) {
        seenPaths.add(m.path);
        factsMissing.push(m);
      }
    }
    // Evidence gaps are per-event and identical across paid kinds; take
    // the max so we report each gap once. Subtract gaps the user has
    // explicitly dismissed on the timeline ("I don't need to act on
    // this") — those events should not block the unlock state.
    const liveGaps = r.evidenceGaps.filter((g) => !dismissedGaps.has(g.eventId));
    if (liveGaps.length > maxEvidenceGaps) {
      maxEvidenceGaps = liveGaps.length;
    }
  }

  return { factsMissing, evidenceGapCount: maxEvidenceGaps };
}

// (Previously: ArtifactStatus rendered the "Latest: … · download again"
// hint inside FreeMessageCard. With the bilingual modal, the modal IS
// the download surface — the inline hint became noise. PackItem has its
// own download-again rendering; this helper is no longer needed.)
